import { useEffect, useRef, useState, type RefObject } from 'react';

export type UsePresenceOptions = {
  /** Fallback unmount delay when transitionend does not fire (ms). */
  ms: number;
  /** Element whose transform/opacity transition signals exit complete. */
  panelRef: RefObject<HTMLElement | null>;
  /** CSS property to wait for on exit (default: transform). */
  propertyName?: string;
  /** Called after the panel unmounts following exit. */
  onExited?: () => void;
};

/**
 * Keep an overlay mounted through its exit transition.
 * Double-rAF on open so CSS transitions run from the closed styles.
 */
export function usePresence(opened: boolean, options: UsePresenceOptions) {
  const { ms, panelRef, propertyName = 'transform', onExited } = options;
  const onExitedRef = useRef(onExited);
  onExitedRef.current = onExited;

  const [mounted, setMounted] = useState(opened);
  const [visible, setVisible] = useState(opened);

  useEffect(() => {
    if (opened) {
      setMounted(true);
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
      return () => cancelAnimationFrame(id);
    }
    setVisible(false);
  }, [opened]);

  useEffect(() => {
    if (!mounted || visible) return;
    const panel = panelRef.current;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      setMounted(false);
      onExitedRef.current?.();
    };
    const onEnd = (e: TransitionEvent) => {
      if (e.target !== panel || e.propertyName !== propertyName) return;
      finish();
    };
    panel?.addEventListener('transitionend', onEnd);
    const fallback = window.setTimeout(finish, ms + 40);
    return () => {
      panel?.removeEventListener('transitionend', onEnd);
      window.clearTimeout(fallback);
    };
  }, [mounted, visible, ms, panelRef, propertyName]);

  return { mounted, visible };
}
